import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button } from '@material-ui/core';
import { Form, Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import i18next from 'i18next';
import Footer from '../../parts/Footer';
import { useStyles } from './styles';
import PublishEventHeader from './parts/PublishEventHeader';
import EventForm from './parts/EventForm';
import { eventFormInitialValues, EventValues, generateValidationSchema } from './parts/EventForm/config';
import PreviewEvent from './parts/PreviewEvent';
import CoverImage from './parts/CoverImage';
import { CreateEventRequestDto } from '../../models/dto/CreateEventRequestDto';
import { createEvent, getTicketTypes } from '../../services/EventService';
import { Ticket } from '../../models/Ticket';

dayjs.extend(utc);

const PublishEventPage: FC = () => {
    const classes = useStyles();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const [coverImage, setCoverImage] = useState<string>('');
    const [ticketTypes, setTicketTypes] = useState<Ticket[]>([]);
    const [chosenTicket, setChosenTicket] = useState<Ticket>();
    const [isPreviewingEvent, setIsPreviewingEvent] = useState<boolean>(false);
    const [formValues, setFormValues] = useState<EventValues>(eventFormInitialValues);

    const validationSchema = useMemo(() => {
        return generateValidationSchema(
            chosenTicket?.max_attendees || 49,
            chosenTicket?.max_ticket_price || 0,
        );
    }, [chosenTicket]);

    useEffect(() => {
        getTicketTypes().then((types: Ticket[]) => {
            setTicketTypes(types);
        });
    }, []);

    useEffect(() => {
        const ticket = ticketTypes.find(
            t =>
                t.event_type === (formValues.type === 'Interactive' ? 0 : 1) &&
                t.rally_ticket === !formValues.pricingEnabled &&
                (formValues.pricingEnabled ? t.ticket_price_enabled === formValues.price > 0 : true),
        );
        setChosenTicket(ticket);
    }, [formValues, ticketTypes]);

    const previewEvent = () => setIsPreviewingEvent(true);
    const handlePreviewEventClose = () => setIsPreviewingEvent(false);

    const imageUrlToBase64 = async (url: string): Promise<any> => {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
            try {
                const reader = new FileReader();
                reader.onload = resolve;
                reader.readAsDataURL(blob);
            } catch (e) {
                reject(e);
            }
        });
    };

    const publishEvent = async (values: EventValues) => {
        const imageData = await imageUrlToBase64(coverImage);
        const requestObject: CreateEventRequestDto = {
            event_cover_image: imageData?.currentTarget?.result || '',
            event_title: values.title,
            event_date: new Date(values.date).getTime(),
            event_start_hour: dayjs(new Date(values.date)).format('HH:mm'),
            event_description: values.description,
            public: values.public,
            event_opening_ticketing: values.pricingEnabled,
            event_rally_coins: values.coins,
            event_rally_nfts_collections: values.nftCollections,
        };

        if (values.type === 'Interactive') {
            requestObject.event_num_attendees = values.tickets || chosenTicket?.max_attendees || 49;
            requestObject.event_price_ticket = values.price;
            requestObject.event_ticket_type = chosenTicket?.type_id || 0;
        } else {
            requestObject.event_num_attendees_streaming =
                values.tickets || chosenTicket?.max_attendees || 1000;
            requestObject.event_price_ticket_streaming = values.price;
            requestObject.event_ticket_type_streaming = chosenTicket?.type_id || 0;
        }

        try {
            console.log(requestObject);
            const eventResponse = await createEvent(requestObject);

            history.push(`/event/${eventResponse.event.event_uuid}`);
        } catch (e: any) {
            enqueueSnackbar(e?.response?.data?.error?.map((error: any) => error.message)?.join(', '), {
                variant: 'error',
            });
        }
    };

    const cancelPublishing = () => history.goBack();

    return (
        <Formik
            enableReinitialize
            initialValues={eventFormInitialValues}
            validationSchema={validationSchema}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={values => publishEvent(values)}
        >
            {({ values, handleChange, setFieldValue, handleSubmit, submitForm }) => {
                setFormValues(values);

                return (
                    <Box flex={1}>
                        <Form onSubmit={handleSubmit}>
                            <PublishEventHeader onCancel={cancelPublishing} submitForm={submitForm} />

                            <Box className={classes.pageContent}>
                                <Box className={classes.form}>
                                    <CoverImage onCoverImageSave={setCoverImage} />
                                    <EventForm
                                        values={values}
                                        handleChange={handleChange}
                                        setFieldValue={setFieldValue}
                                        maximumTicketsNumber={chosenTicket?.max_attendees || 49}
                                    />

                                    <Box className={classes.buttonsContainer}>
                                        <Button
                                            className={clsx(
                                                classes.checkOutOrPublishButton,
                                                classes.checkOutButton,
                                            )}
                                            variant="outlined"
                                            onClick={previewEvent}
                                        >
                                            {i18next.t('InfluencerPublishSeeEvent')}
                                        </Button>
                                        <Button
                                            className={clsx(
                                                classes.checkOutOrPublishButton,
                                                classes.publishButton,
                                            )}
                                            onClick={submitForm}
                                        >
                                            {i18next.t('InfluencerPublishPublishEvent')}
                                        </Button>
                                    </Box>
                                </Box>
                            </Box>

                            <Footer />
                        </Form>

                        <PreviewEvent
                            isOpen={isPreviewingEvent}
                            onClose={handlePreviewEventClose}
                            event={values}
                            coverImage={coverImage}
                        />
                    </Box>
                );
            }}
        </Formik>
    );
};

export default PublishEventPage;
